import React, {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {isOk} from '../../utils/apiResponse';
import {Spinner} from '../Spinner';
import {listAgencyClients} from './connectionLinksApi';

/**
 * Elegir el cliente cuando la puerta no lo trae (Conexiones de la agencia). Lo usan «Enviar link al vendedor»
 * y la invitación al Portal del paso previo de Mercado Ads: el mismo listado y el mismo selector. Son los
 * clientes activos que el usuario puede gestionar (un no admin ve sólo los suyos: lo resuelve el backend),
 * por nombre. El estilo lo pone quien lo monta (`.scl-modal` en el modal, `.ma-choose__portal` en el paso).
 */
export const CLIENTS_LOAD_ERROR = 'LOAD_ERROR';

export function activeClientsSorted(clients) {
    return clients
        .filter((client) => client.active !== false)
        .sort((a, b) => String(a.name || '').localeCompare(String(b.name || '')));
}

// El select trabaja con strings; el backend espera el id como número.
export function asClientId(value) {
    return /^\d+$/.test(String(value)) ? Number(value) : value;
}

/** Los clientes de la agencia: se piden cuando `active` se prende, y otra vez si la carga anterior falló. */
export function useAgencyClients(active) {
    const [clients, setClients] = useState(null);

    const load = useCallback(() => {
        setClients(null);
        listAgencyClients().then((response) => {
            setClients(isOk(response) && Array.isArray(response.data)
                ? activeClientsSorted(response.data)
                : CLIENTS_LOAD_ERROR);
        });
    }, []);

    useEffect(() => {
        if (active && (clients === null || clients === CLIENTS_LOAD_ERROR)) load();
        // Sólo al prenderse: un error de carga se reintenta con su botón, no en cada render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active]);

    return [clients, load];
}

/** El selector con sus estados: cargando, error con «Volver a intentar», sin clientes y la lista. */
export function AgencyClientPicker({id, clients, value, onChange, onRetry}) {
    const {t} = useTranslation();
    if (clients === null) {
        return <div className="scl-inline-loader"><Spinner color="positive small"/></div>;
    }
    if (clients === CLIENTS_LOAD_ERROR) {
        return (
            <p className="scl-error" role="alert">
                {t('mercado_ads_link.send.clients_load_error')}{' '}
                <button type="button" className="scl-text-btn" onClick={onRetry}>
                    {t('mercado_ads_link.send.retry')}
                </button>
            </p>
        );
    }
    if (clients.length === 0) {
        return <p className="scl-hint">{t('mercado_ads_link.send.no_clients')}</p>;
    }
    return (
        <div className="scl-field">
            <label htmlFor={id} className="scl-label">{t('mercado_ads_link.send.client_label')}</label>
            <select id={id} className="form-control" value={value} onChange={(event) => onChange(event.target.value)}>
                <option value="">{t('mercado_ads_link.send.client_placeholder')}</option>
                {clients.map((client) => (
                    <option key={client.id} value={String(client.id)}>{client.name}</option>
                ))}
            </select>
        </div>
    );
}

export default AgencyClientPicker;
